import React, { useMemo, useState } from 'react';
import { isNil } from 'lodash';
import { Form, Formik } from 'formik';
import { RequestStatus } from 'src/components/AuthNew/types';
import { AuthSuccess } from 'src/components/AuthNew/components/AuthSuccess';
import {
  getPasswordValidateMessage,
  passwordIsValid,
} from 'src/components/AuthNew/utils/validations';
import styles from './passwordCreateForm.module.css';
import type { FormValues } from './types';
import { FormFields } from './types';
import { Title } from '../Title';
import { Description } from '../Description';
import { Content } from '../Content';
import { ButtonWithLoader } from '../ButtonWithLoader/ButtonWithLoader';
import { PasswordInput } from '../PasswordInput';
import type { HintItem } from './PasswordHint';
import { PasswordHint } from './PasswordHint';

interface Props {
  isMailConfirmProccessed?: boolean;
  requestStatus: RequestStatus;
  onSubmit: (PostSignup) => Promise<void>;
}

const initialValues: FormValues = {
  password: '',
  password_confirmation: '',
};

const PASSWORD_MIN_LENGTH = 8;

export const PasswordCreateForm: React.FC<Props> = ({
  isMailConfirmProccessed,
  requestStatus,
  onSubmit,
}) => {
  const [isSecure, setIsSecure] = useState([true, true]);

  const [hasDigit, setHasDigit] = useState(false);
  const [hasCharacter, setHasCharacter] = useState(false);
  const [isEnoughLength, setIsEnoughLength] = useState(false);

  const hintItems: HintItem[] = useMemo(
    () => [
      {
        name: `${PASSWORD_MIN_LENGTH} символов`,
        checked: isEnoughLength,
      },
      {
        name: 'Цифры',
        checked: hasDigit,
      },
      {
        name: 'Буквы',
        checked: hasCharacter,
      },
    ],
    [hasDigit, isEnoughLength, hasCharacter],
  );

  const handleSubmit = async ({
    password,
    password_confirmation,
  }: FormValues) => {
    if (isNil(password) || isNil(password_confirmation)) {
      return;
    }

    setIsSecure([true, true]);

    await onSubmit({ password, password_confirmation });
  };

  function containsAtLeastOneDigit(password: string) {
    return /[0-9]/.test(password);
  }

  function containsAtLeastOneCharacter(password: string) {
    return /[a-zA-Z]/.test(password);
  }

  function checkIsEnoughLength(password: string) {
    return password.length >= PASSWORD_MIN_LENGTH;
  }

  const handlePasswordChange = (value: string) => {
    setHasDigit(containsAtLeastOneDigit(value));
    setHasCharacter(containsAtLeastOneCharacter(value));
    setIsEnoughLength(checkIsEnoughLength(value));
  };

  function isValid(values: FormValues) {
    return [
      hasCharacter,
      hasDigit,
      isEnoughLength,
      values[FormFields.Password] === values[FormFields.PasswordConfirmation],
      passwordIsValid(values[FormFields.Password] || ''),
    ].every((item) => Boolean(item));
  }

  function toggleSecure(i) {
    const newArr = [...isSecure];
    newArr[i] = !newArr[i];
    setIsSecure(newArr);
  }

  function validatePasswordConfirm(passConfirm: string, pass?: string) {
    return passConfirm === pass ? undefined : 'Пароли не совпадают';
  }

  const TITLE = isMailConfirmProccessed
    ? 'Почта подтверждена'
    : 'Установите пароль';

  const DESCRIPTION = isMailConfirmProccessed
    ? 'Завершите процесс подтверждения почты, установив пароль'
    : 'для входа без СМС-подтверждения';

  return (
    <Formik<FormValues> initialValues={initialValues} onSubmit={handleSubmit}>
      {({ isSubmitting, values }) => (
        <Form>
          <Title>{TITLE}</Title>
          <Description>{DESCRIPTION}</Description>
          <Content>
            <PasswordInput
              className={styles.passwordInput}
              name={FormFields.Password}
              header="Пароль"
              placeholder="Введите пароль"
              isSecure={isSecure[0]}
              postfixIconProps={{
                onClick: () => toggleSecure(0),
              }}
              onChange={handlePasswordChange}
              hint={<PasswordHint items={hintItems} />}
              validate={getPasswordValidateMessage}
            />
            <PasswordInput
              className={styles.passwordInput}
              name={FormFields.PasswordConfirmation}
              header="Подтверждение пароля"
              placeholder="Повторите пароль"
              isSecure={isSecure[1]}
              postfixIconProps={{
                onClick: () => toggleSecure(1),
              }}
              validate={(value) =>
                validatePasswordConfirm(value, values[FormFields.Password])
              }
            />
            <ButtonWithLoader
              primary
              type="submit"
              isLoading={isSubmitting}
              disabled={isSubmitting || !isValid(values)}
              width="100%"
            >
              Установить пароль
            </ButtonWithLoader>
          </Content>
          {requestStatus === RequestStatus.Succeeded && (
            <AuthSuccess label="Пароль установлен" />
          )}
        </Form>
      )}
    </Formik>
  );
};
